import { z } from 'zod';
import { IsoDateTime, Role, Uuid } from './common';

export const UserStatus = z.enum(['invited', 'active', 'disabled']);
export type UserStatus = z.infer<typeof UserStatus>;

export const User = z
  .object({
    id: Uuid,
    name: z.string(),
    email: z.email(),
    role: Role,
    status: UserStatus,
    activeSessions: z.number().int().describe('Open (not expired, not revoked) sessions.'),
    twoFactorEnabled: z.boolean().describe('Two-step verification (authenticator app) is on.'),
    lastSeenAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({
    id: 'User',
    example: {
      id: '5b0c6f9e-2d3a-4a51-9a52-4f0d3c8a1b11',
      name: 'Lucas',
      email: 'lucas@example.com',
      role: 'admin',
      status: 'active',
      activeSessions: 2,
      twoFactorEnabled: true,
      lastSeenAt: '2026-10-06T17:30:00.000Z',
      createdAt: '2026-10-06T12:00:00.000Z',
      updatedAt: '2026-10-06T12:00:00.000Z',
    },
  });
export type User = z.infer<typeof User>;

export const UserList = z.object({ data: z.array(User) });

export const CreateUserBody = z
  .object({
    name: z.string().trim().min(1).max(120),
    email: z.email().trim().toLowerCase(),
    role: Role,
  })
  .meta({ example: { name: 'Operadora', email: 'operadora@example.com', role: 'operator' } });
export type CreateUserBody = z.infer<typeof CreateUserBody>;

export const UpdateUserBody = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    role: Role.optional(),
  })
  .refine((b) => b.name !== undefined || b.role !== undefined, 'Nothing to update');
export type UpdateUserBody = z.infer<typeof UpdateUserBody>;

/** One-time link the Admin copies and sends (no email delivery yet). */
export const AuthLink = z
  .object({
    url: z.string().describe('Full URL to open in the browser.'),
    token: z.string().describe('The one-time token inside the URL. Shown only once.'),
    expiresAt: IsoDateTime,
  })
  .meta({ id: 'AuthLink' });
export type AuthLink = z.infer<typeof AuthLink>;

export const CreateUserResponse = z.object({ user: User, invite: AuthLink });
export type CreateUserResponse = z.infer<typeof CreateUserResponse>;

export const Session = z
  .object({
    id: Uuid,
    userAgent: z.string().nullable(),
    device: z
      .string()
      .describe('Short human label derived from the user agent, e.g. "Chrome · macOS".'),
    ip: z.string().nullable(),
    persistent: z.boolean().describe('"Manter conectado" was ticked (30 days).'),
    current: z.boolean().describe('The session making this request.'),
    createdAt: IsoDateTime,
    lastSeenAt: IsoDateTime,
    expiresAt: IsoDateTime,
  })
  .meta({ id: 'Session' });
export type Session = z.infer<typeof Session>;

export const SessionList = z.object({ data: z.array(Session) });
